import Markdown from 'markdown-to-jsx';
import { IProgression } from '../../../models/course/ICourseData';
import CourseProgression from '../course-progression/course-progression';
import styles from './course-progressions-list.module.scss';
import React, { useCallback, useEffect, useState } from 'react';

type Props = {
  countryCode: string;
  progressions: IProgression[];
  pagingLimit: number;
  programRoute: String;
  progressionNote?: string;
};

export default function CourseProgressionsList({
  countryCode,
  progressions,
  pagingLimit,
  programRoute,
  progressionNote,
}: Props) {
  const [searchInput, setSearchInput] = useState('');
  const [scrollValue, setScrollValue] = useState<number>(0);
  const showSearchInput = progressions?.length > 1;
  let [progressionList, setProgressions] = useState<IProgression[]>(
    progressions.slice(0, pagingLimit),
  );

  useEffect(() => {
    setProgressions(progressions.slice(0, pagingLimit));
  }, [pagingLimit, progressions]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault();
    setSearchInput(e.target.value);
  }, []);

  if (searchInput.length > 2) {
    progressions = progressions.filter((progression) => {
      return progression.programName.toLowerCase().match(searchInput.toLowerCase());
    });
    progressionList = progressions.slice(0, pagingLimit);
  }

  const onScroll = useCallback((event) => {
    const {
      target: { body, documentElement },
    } = event;
    const scrollTop = body.scrollTop || documentElement.scrollTop;
    const height = documentElement.scrollHeight - documentElement.clientHeight;
    const scrolled = scrollTop / height;
    setScrollValue(scrolled);
  }, []);

  useEffect(() => {
    window.addEventListener(`scroll`, onScroll);

    return () => {
      window.removeEventListener(`scroll`, onScroll);
    };
  }, [onScroll]);

  const loadProgressionItems = useCallback(() => {
    const length = progressionList != null ? progressionList?.length : 0;
    const progressionRemainingLength = progressionList?.length + pagingLimit;
    const remainingProgression = Array.from(progressions).slice(length, progressionRemainingLength);
    setProgressions([...progressionList, ...remainingProgression]);
  }, [progressionList, pagingLimit, progressions]);

  useEffect(() => {
    const length = progressionList != null ? progressionList?.length : 0;
    if (scrollValue > 0.95 && length < progressions.length) {
      loadProgressionItems();
    }
  }, [loadProgressionItems, progressionList, progressions.length, scrollValue]);

  return (
    <div>
      <div className='row'>
        {/* Hide Progression Search if there is only one progression initially */}
        {showSearchInput && (
          <div className='col-md-6'>
            <input
              type='search'
              className={styles[`txtBox`]}
              onChange={handleChange}
              value={searchInput}
              placeholder='Start typing progression name'
              aria-label='start typing here to search for your desired progression'
            />
          </div>
        )}
        {programRoute !== 'ICAS' && (
          <div className='col-md-6 mt-md-0 mt-3'>
            <div>
              <p className={`${styles[`progression-legend`]} ${styles[`bg-blue`]}`}>P</p>
              <span className={styles[`secondary-text`]}> = Placement option available</span>
            </div>
            <div>
              <p className={`${styles[`progression-legend`]} ${styles[`bg-green`]}`}>A</p>
              <span className={styles[`secondary-text`]}> = Study abroad option available</span>
            </div>
          </div>
        )}
      </div>
      {progressionNote && (
        <p className='mb-0 mt-4'>
          <Markdown>{progressionNote}</Markdown>
        </p>
      )}
      {progressionList?.length > 0 ? (
        progressionList.map((progression) => (
          <CourseProgression
            countryCode={countryCode}
            key={progression.productProgressionId}
            progression={progression}
            programRoute={programRoute}
          />
        ))
      ) : (
        <div>
          <p>No results found, please search again</p>
        </div>
      )}
    </div>
  );
}
